import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { CalendarClock, FileSpreadsheet, Pencil, Save, Siren, Trash2 } from 'lucide-react';
import {
  Button,
  DataTable,
  DateField,
  ErrorState,
  FormField,
  FormGrid,
  Loading,
  PageHeader,
  Panel,
  RadioGroup,
  StatusBadge,
  Textarea,
  useConfirm,
  useToast,
  type Column,
} from '@/components';
import { useCurrentUser } from '@/auth/AuthProvider';
import { hasRole } from '@/auth/types';
import { useFiscalYear } from '@/fiscal-year/FiscalYearProvider';
import { errorMessage } from '@/lib/api';
import { downloadFile } from '@/lib/download';
import { formatDateTime } from '@/lib/format';
import { toWareki } from '@/lib/wareki';
import { changeTypeLabel, hm, useDeleteNotice, useFundReview, useNotice, type NoticeDetail, type NoticeVesselDto } from './api';
import { REG_TONE } from './AdvanceNoticeListPage';
import { FixedReportDatePicker } from './components/FixedReportDatePicker';
import './advance-notices.css';

const CHANGE_TONE = { 変更無し: 'neutral', 変更: 'warning', 追加: 'info', 削除: 'danger' } as const;
const yn = (b: boolean | undefined) => (b ? '有' : '無');

/** S-22 事前連絡 詳細 */
export function AdvanceNoticeDetailPage() {
  const { id } = useParams();
  const { data, isPending, error, refetch } = useNotice(Number(id));
  if (isPending) return <Loading block />;
  if (error || !data) return <ErrorState error={error} onRetry={() => void refetch()} />;
  return <Detail d={data} />;
}

function Detail({ d }: { d: NoticeDetail }) {
  const user = useCurrentUser();
  const canEdit = hasRole(user, ['基金', '漁協']);
  const isFund = hasRole(user, ['基金']);
  const { yearPath } = useFiscalYear();
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const remove = useDeleteNotice();
  const [pickRow, setPickRow] = useState<number | null>(null);

  const onPrint = async () => {
    try {
      const name = await downloadFile(`/advance-notices/${d.id}/report.xlsx`);
      toast.success('事前連絡票を出力しました', name);
    } catch (e) {
      toast.error('帳票を出力できませんでした', errorMessage(e));
    }
  };
  const onDelete = async () => {
    if (!(await confirm({ title: '事前連絡を削除しますか？', message: `${d.noticeNo} の事前連絡と実施船・申請履歴を削除します。\nこの操作は取り消せません。`, tone: 'danger', confirmLabel: '削除する' })))
      return;
    try {
      await remove.mutateAsync(d.id);
      toast.success('事前連絡を削除しました');
      navigate(yearPath('advance-notices'));
    } catch (e) {
      toast.error('削除できませんでした', errorMessage(e));
    }
  };

  const reportable = (v: NoticeVesselDto) => canEdit && v.vesselId != null && v.changeType !== '削除' && d.registrationType !== '中止';
  const columns: Column<NoticeVesselDto>[] = [
    { key: 'sortOrder', header: 'No', width: 48, numeric: true, render: (v) => v.sortOrder + 1 },
    {
      key: 'vesselName',
      header: '実施船名（電話番号）',
      nowrap: true,
      render: (v) => (
        <>
          <span className={v.vesselId == null ? 'bs-unlisted' : v.changeType === '削除' ? 'bs-cell-strike' : undefined}>{v.vesselName ?? v.unlistedVesselName}</span>
          <span className="bs-cell-sub bs-num">{[v.registrationNo, v.phone].filter(Boolean).join(' ／ ') || (v.vesselId == null ? v.unlistedReason ?? '名簿外' : '')}</span>
        </>
      ),
    },
    { key: 'crewCount', header: '乗組員', width: 70, numeric: true, render: (v) => `${v.crewCount}人` },
    { key: 'portName', header: '港名', width: 90 },
    {
      key: 'departureDate',
      header: '出港日時',
      width: 150,
      render: (v) => (
        <>
          {toWareki(v.departureDate, { style: 'short' })}
          <span className="bs-cell-sub bs-num">{hm(v.departureHour, v.departureMinute)}</span>
        </>
      ),
    },
    {
      key: 'arrivalDate',
      header: '入港日時',
      width: 150,
      render: (v) => (
        <>
          {toWareki(v.arrivalDate, { style: 'short' })}
          <span className="bs-cell-sub bs-num">{hm(v.arrivalHour, v.arrivalMinute)}</span>
        </>
      ),
    },
    { key: 'zoneNumbers', header: '農林漁区番号', width: 120, render: (v) => <span className="bs-num">{v.zoneNumbers.split('.').join('・')}</span> },
    { key: 'catchSurveyPlanned', header: '漁獲調査', width: 70, align: 'center', render: (v) => yn(v.catchSurveyPlanned) },
    { key: 'catchSalePlanned', header: '販売', width: 56, align: 'center', render: (v) => yn(v.catchSalePlanned) },
    { key: 'changeType', header: '変更区分', width: 84, render: (v) => <StatusBadge tone={CHANGE_TONE[v.changeType]}>{changeTypeLabel(v.changeType)}</StatusBadge> },
    {
      key: 'ops',
      header: '',
      width: 190,
      render: (v) =>
        reportable(v) ? (
          <div className="bs-row-actions">
            <Button size="sm" icon={<CalendarClock size={14} />} onClick={() => setPickRow(v.id)}>
              定時連絡
            </Button>
            <Button size="sm" variant="accent" icon={<Siren size={14} />} onClick={() => navigate(yearPath(`voyage-reports/new?noticeVesselId=${v.id}`))}>
              速報
            </Button>
          </div>
        ) : null,
    },
  ];

  return (
    <>
      <PageHeader
        eyebrow="日々の報告 ／ 事前連絡"
        title={`事前連絡 ${d.noticeNo}`}
        meta={
          <>
            <StatusBadge tone={REG_TONE[d.registrationType]} solid>
              {d.registrationType}
            </StatusBadge>
            <StatusBadge tone={d.approvalStatus === '承認' ? 'success' : 'info'} dot>
              {d.approvalStatus}
            </StatusBadge>
          </>
        }
        description={`${d.businessOperatorName}　報告者：${d.reporterName}　報告日：${toWareki(d.reportDate)}`}
        actions={
          <>
            <Button icon={<FileSpreadsheet size={16} />} onClick={() => void onPrint()}>
              帳票印刷
            </Button>
            {canEdit && (
              <>
                <Button variant="danger" icon={<Trash2 size={16} />} onClick={() => void onDelete()} loading={remove.isPending}>
                  削除
                </Button>
                <Button variant="primary" icon={<Pencil size={16} />} onClick={() => navigate(yearPath(`advance-notices/${d.id}/edit`))}>
                  変更・中止
                </Button>
              </>
            )}
          </>
        }
      />
      <Panel title="連絡内容">
        <dl className="bs-kv">
          <div>
            <dt>連絡書番号</dt>
            <dd className="bs-num">{d.noticeNo}</dd>
          </div>
          <div>
            <dt>事業実施者</dt>
            <dd>{d.businessOperatorName}</dd>
          </div>
          <div>
            <dt>変更・中止申請日</dt>
            <dd>{d.changeAppliedOn ? toWareki(d.changeAppliedOn) : '—'}</dd>
          </div>
          <div>
            <dt>最終更新</dt>
            <dd className="bs-num">{formatDateTime(d.updatedAt)}</dd>
          </div>
          <div className="is-full">
            <dt>変更理由</dt>
            <dd>{d.changeReason || '—'}</dd>
          </div>
        </dl>
      </Panel>
      <Panel title="実施船" flush>
        <DataTable caption="実施船" columns={columns} rows={d.vessels} rowKey={(v) => v.id} dense rowClassName={(v) => (v.changeType === '削除' ? 'is-muted' : undefined)} />
      </Panel>
      <FundReviewPanel d={d} editable={isFund} />
      <Panel title="申請履歴" description="保存のたびに記録されます。「変更前の実施船」で保存前の内容を確認できます。">
        <ol className="bs-history">
          {[...d.histories].reverse().map((h) => (
            <li key={h.id} className="bs-history__item">
              <div className="bs-history__meta">
                <span className="bs-history__when">{formatDateTime(h.appliedAt)}</span>
                <StatusBadge tone={REG_TONE[h.registrationType]}>{h.registrationType}</StatusBadge>
                <span>報告者：{h.reporterName}</span>
              </div>
              {h.changeReason && <p className="bs-history__reason">変更理由：{h.changeReason}</p>}
              {h.beforeSnapshot.length > 0 && (
                <details>
                  <summary>変更前の実施船（{h.beforeSnapshot.length}隻）</summary>
                  <table className="bs-mini-table">
                    <thead>
                      <tr>
                        <th>実施船名</th>
                        <th>乗組員</th>
                        <th>港名</th>
                        <th>出港</th>
                        <th>入港</th>
                        <th>農林漁区番号</th>
                        <th>変更区分</th>
                      </tr>
                    </thead>
                    <tbody>
                      {h.beforeSnapshot.map((v, i) => (
                        <tr key={i}>
                          <td>{v.vesselName ?? v.unlistedVesselName}</td>
                          <td>{v.crewCount}人</td>
                          <td>{v.portName}</td>
                          <td className="bs-num">
                            {v.departureDate} {hm(v.departureHour, v.departureMinute)}
                          </td>
                          <td className="bs-num">
                            {v.arrivalDate} {hm(v.arrivalHour, v.arrivalMinute)}
                          </td>
                          <td className="bs-num">{v.zoneNumbers}</td>
                          <td>{changeTypeLabel(v.changeType)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </details>
              )}
            </li>
          ))}
        </ol>
      </Panel>
      <FixedReportDatePicker rowId={pickRow} onClose={() => setPickRow(null)} />
    </>
  );
}

/** 基金記入欄（承認区分・変更中止承認日・基金からのコメント）。基金のみ編集可 */
function FundReviewPanel({ d, editable }: { d: NoticeDetail; editable: boolean }) {
  const toast = useToast();
  const save = useFundReview(d.id);
  const { register, control, handleSubmit, formState } = useForm({
    defaultValues: { approvalStatus: d.approvalStatus, changeApprovedOn: d.changeApprovedOn ?? '', fundComment: d.fundComment ?? '' },
  });
  const onSubmit = handleSubmit(async (v) => {
    try {
      await save.mutateAsync({ approvalStatus: v.approvalStatus, changeApprovedOn: v.changeApprovedOn || null, fundComment: v.fundComment || null });
      toast.success('基金記入欄を更新しました');
    } catch (e) {
      toast.error('保存できませんでした', errorMessage(e));
    }
  });
  if (!editable) {
    return (
      <Panel title="基金記入欄">
        <dl className="bs-kv">
          <div>
            <dt>承認区分</dt>
            <dd>{d.approvalStatus}</dd>
          </div>
          <div>
            <dt>変更・中止承認日</dt>
            <dd>{d.changeApprovedOn ? toWareki(d.changeApprovedOn) : '—'}</dd>
          </div>
          <div className="is-wide">
            <dt>基金からのコメント</dt>
            <dd>{d.fundComment || '—'}</dd>
          </div>
        </dl>
      </Panel>
    );
  }
  return (
    <form onSubmit={onSubmit} noValidate className="bs-fund-form">
      <Panel
        title="基金記入欄"
        actions={
          <Button type="submit" size="sm" variant="primary" icon={<Save size={15} />} loading={formState.isSubmitting} disabled={!formState.isDirty}>
            保存する
          </Button>
        }
      >
        <FormGrid columns={4}>
          <FormField label="承認区分">
            <Controller
              control={control}
              name="approvalStatus"
              render={({ field }) => <RadioGroup name={field.name} value={field.value} onChange={field.onChange} options={[{ value: '承認', label: '承認' }, { value: '承認待ち', label: '承認待ち' }]} />}
            />
          </FormField>
          <FormField label="変更・中止承認日">
            <DateField showWareki {...register('changeApprovedOn')} />
          </FormField>
          <FormField label="基金からのコメント" span={2}>
            <Textarea rows={2} {...register('fundComment')} />
          </FormField>
        </FormGrid>
      </Panel>
    </form>
  );
}
